import React from 'react';

const AppSafetyStandardsPage = () => {
    return (
        <div className="min-h-screen flex flex-col bg-white font-sans text-gray-800">
            {/* Simple Header for Branding */}
            <div className="bg-white shadow-sm border-b border-gray-100 py-6 sticky top-0 z-10">
                <div className="container mx-auto px-4 flex justify-center items-center">
                    <img src="/images/logo.png" alt="MarryCenter Logo" className="h-10 w-auto mr-3" />
                    <span className="text-3xl font-bold bg-gradient-to-r from-romantic-600 to-primary-600 bg-clip-text text-transparent" style={{ fontFamily: 'Helvetica, Arial, sans-serif' }}>
                        MarryCenter
                    </span>
                </div>
            </div>

            <div className="flex-grow py-8 bg-gray-50">
                <div className="w-full max-w-[95%] mx-auto bg-white p-8 md:p-12 rounded-xl shadow-sm">
                    <h1 className="text-4xl md:text-5xl font-bold mb-10 text-gray-900 border-b pb-6">Safety Standards & Guidelines</h1>

                    <div className="space-y-8 text-lg md:text-xl leading-relaxed text-gray-700">
                        <section>
                            <p className="mb-6">
                                At MarryCenter, the safety and well-being of our community is our highest priority. We are committed to maintaining a respectful, secure, and authentic environment for finding a life partner. This document outlines our safety standards, prohibited content, reporting mechanisms, and enforcement policies.</p>
                        </section>

                        <section>
                            <h2 className="text-3xl font-bold mb-6 text-gray-900">1. Prohibited Content & Behavior</h2>
                            <p className="mb-6">
                                To ensure a safe experience for all users, the following content and behavior are strictly prohibited. Violations may result in suspension or permanent termination.
                            </p>
                            <ul className="list-disc pl-8 space-y-4 mb-8">
                                <li><strong>Hate Speech & Discrimination:</strong> Content promoting hatred or violence based on race, religion, gender, disability, nationality, age, or sexual orientation.</li>
                                <li><strong>Harassment & Bullying:</strong> Threats, stalking, intimidation, or abusive behavior.</li>
                                <li><strong>Sexual Content & Nudity:</strong> Pornography, explicit material, or nudity in profiles, messages, or shared media.</li>
                                <li><strong>Underage Users:</strong> Users must be 18+. Profiles involving minors are prohibited.</li>
                                <li><strong>Spam & Scams:</strong> Financial solicitation, fraud, misleading links, or commercial promotion.</li>
                                <li><strong>Impersonation:</strong> Fake profiles or pretending to be another person.</li>
                                <li><strong>Illegal Activities:</strong> Content promoting illegal or criminal activities is prohibited.</li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="text-3xl font-bold mb-6 text-gray-900">2. Zero-Tolerance for Child Sexual Abuse & Exploitation (CSAE)</h2>
                            <p className="mb-6 font-medium">
                                MarryCenter is intended only for users aged 18 and above.
                            </p>
                            <p className="mb-6">
                                We enforce a strict zero-tolerance policy toward Child Sexual Abuse and Exploitation (CSAE).
                            </p>
                            <p className="mb-4">
                                In compliance with legal and Google Play requirements:
                            </p>
                            <ul className="list-disc pl-8 space-y-4 mb-8">
                                <li>Any identified Child Sexual Abuse Material (CSAM) is removed immediately.</li>
                                <li>Relevant data is preserved securely in compliance with applicable data protection laws.</li>
                                <li>Confirmed cases are reported to appropriate authorities, including NCMEC and law enforcement agencies.</li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="text-3xl font-bold mb-6 text-gray-900">3. In-App Reporting Tools</h2>
                            <p className="mb-6">
                                Users are encouraged to report inappropriate behavior directly within the app.
                            </p>
                            <h3 className="text-2xl font-bold mb-4 text-gray-800">How to Report:</h3>
                            <ul className="list-disc pl-8 space-y-4 mb-8">
                                <li><strong>Profiles:</strong> Open profile → Tap “Report”</li>
                                <li><strong>Messages:</strong> Long press on any message to open the report option → Select “Report”</li>
                                <li><strong>Posts:</strong> Tap the three-dot menu → Select “Report”</li>
                            </ul>
                            <p className="mb-6">
                                All reports are confidential and anonymous. Users may select a reason and provide optional details.
                            </p>
                        </section>

                        <section>
                            <h2 className="text-3xl font-bold mb-6 text-gray-900">4. Moderation Process & Response Times</h2>
                            <p className="mb-6">
                                Our moderation team reviews reports continuously.
                            </p>
                            <ul className="list-disc pl-8 space-y-4 mb-8">
                                <li><strong>Review Time:</strong> Within 24 hours</li>
                                <li><strong>Priority Cases:</strong> Safety threats and CSAE reports are handled immediately</li>
                                <li><strong>Actions:</strong> Content removal, warnings, suspensions, permanent bans, and device restrictions</li>
                                <li><strong>Appeals:</strong> Users may contact support if they believe enforcement was incorrect</li>
                                <li><strong>False Reports:</strong> Repeated misuse of reporting tools may result in restrictions</li>
                            </ul>
                        </section>

                        <section>
                            <h2 className="text-3xl font-bold mb-6 text-gray-900">5. Safety Contact</h2>
                            <p className="mb-6">
                                For urgent safety concerns, contact:
                            </p>
                            <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 inline-block">
                                <p className="mb-2"><strong>Email:</strong> <a href="mailto:shahidadmin@marrycenter.com" className="text-blue-600 hover:underline">shahidadmin@marrycenter.com</a></p>
                                <p><strong>Response Time:</strong> We strive to respond to all safety-related inquiries within 24 hours.</p>
                            </div>
                        </section>

                        <div className="pt-8 mt-12 border-t border-gray-200 text-gray-500 text-base">
                            Safety Standards Last updated: February 1, 2026.
                        </div>
                    </div>
                </div>
            </div>

            {/* Simple Copyright Footer */}
            <div className="bg-gray-50 py-8 border-t border-gray-100 mt-auto">
                <div className="container mx-auto px-4 text-center text-gray-500 text-base">
                    <p className="mb-2">&copy; 2026 MarryCenter. All rights reserved.</p>
                    <p className="mt-2">
                        For details on personal data handling, please review our <a href="https://marrycenter.com/app/privacy" className="text-primary-600 hover:underline">Privacy Policy</a>.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default AppSafetyStandardsPage;
